/**
 * 에셋 슬롯 레지스트리.
 *
 * 하나의 슬롯 = (텍스처 키, 기본 파일 경로, 프레임 규격, 기본 도트 아트 생성 함수).
 * 부팅 시 public/assets/<file> 이 있으면 그 파일을 쓰고, 없으면 generate() 로 굽는다.
 * 규격표는 docs/assets-guide.md 에 있으며 이 파일이 그 표의 원본이다.
 */

import { blank, size, stamp, type Grid, type Palette } from '../art/grid';
import { MONSTER_ART } from '../art/monsters';
import { GEAR_LOOK_ART } from '../art/gearLook';
import { BASE_PALETTE } from '../art/palette';
import { PLAYER_ANIMS, PLAYER_FRAME } from '../art/player';
import {
  DIGIT_CELL,
  FX_PALETTE,
  ITEM_ART,
  ITEM_PALETTE,
  SKILL_ICON_ART,
  STATUS_ICON_ART,
  blastFrames,
  boltFrames,
  arrowFrames,
  starFrames,
  digitFont,
  hitFrames,
  levelUpFrames,
  missWord,
  slashFrames,
  sporeFrames,
} from '../art/ui';
import {
  TERRAIN_PALETTE,
  bigMushroomArt,
  bushArt,
  cloudArt,
  flowerArt,
  grassTile,
  ladderTile,
  mushroomHouseArt,
  platformTile,
  portalFrames,
  ropeTile,
  signpostArt,
  soilTile,
  treeArt,
} from '../art/world';

export interface AssetSlot {
  /** Phaser 텍스처 키 */
  key: string;
  /** public/assets/ 기준 기본 파일 경로 */
  file: string;
  /** 기본 프레임 수 */
  frames: number;
  /** 기본 아트를 굽는 배율 (아트 1픽셀 → 화면 n픽셀) */
  scale: number;
  /** 기본 도트 아트 프레임 (아트 좌표계) */
  grids: () => Grid[];
  /** 팔레트 */
  palette: Palette;
  /** 화면에 그려질 높이(px). 오버라이드 스프라이트 크기 정규화에 쓴다. 생략 시 프레임 높이 */
  displayHeight?: number;
  /** 가이드 문서 분류 */
  group: string;
}

function slot(s: AssetSlot): AssetSlot {
  return s;
}

/** 아이템 아이콘을 16x16 정사각형 캔버스 중앙에 정렬 */
function itemGrid(build: () => Grid): Grid {
  const src = build();
  const g = blank(16, 16);
  const s = size(src);
  stamp(g, src, Math.floor((16 - s.w) / 2), Math.floor((16 - s.h) / 2));
  return g;
}

const PLAYER_SLOTS: AssetSlot[] = (
  [
    ['player-stand', 'stand', PLAYER_ANIMS.stand],
    ['player-walk', 'walk', PLAYER_ANIMS.walk],
    ['player-jump', 'jump', PLAYER_ANIMS.jump],
    ['player-hurt', 'hurt', PLAYER_ANIMS.hurt],
    ['player-attack', 'attack', PLAYER_ANIMS.attack],
    ['player-swing', 'swing', PLAYER_ANIMS.swing],
    ['player-ladder', 'ladder', PLAYER_ANIMS.ladder],
    ['player-rope', 'rope', PLAYER_ANIMS.rope],
  ] as const
).map(([key, file, build]) =>
  slot({
    key,
    file: `player/${file}.png`,
    frames: build().length,
    scale: 3,
    grids: build,
    palette: BASE_PALETTE,
    displayHeight: PLAYER_FRAME.height * 3,
    group: 'player',
  }),
);

const MONSTER_SLOTS: AssetSlot[] = (
  [
    ['mob-snail', 'snail', 3],
    ['mob-blue-snail', 'blue-snail', 3],
    ['mob-slime', 'slime', 3],
    ['mob-blue-slime', 'blue-slime', 3],
    ['mob-orange-mushroom', 'orange-mushroom', 3],
    ['mob-horny-mushroom', 'horny-mushroom', 4],
    ['mob-pig', 'pig', 3],
    ['mob-ribbon-pig', 'ribbon-pig', 3],
    ['mob-stump', 'stump', 3],
    ['mob-green-mushroom', 'green-mushroom', 3],
    ['mob-zombie-mushroom', 'zombie-mushroom', 3],
    ['mob-jr-necki', 'jr-necki', 3],
    ['mob-curse-eye', 'curse-eye', 3],
    ['mob-lupin', 'lupin', 3],
    ['mob-stone-bug', 'stone-bug', 3],
    ['mob-star-pixie', 'star-pixie', 3],
    ['mob-luna-pixie', 'luna-pixie', 3],
    ['mob-jr-yeti', 'jr-yeti', 3],
    ['mob-pepe', 'pepe', 3],
    ['mob-tick-tock', 'tick-tock', 3],
    ['mob-clock', 'clock', 3],
    ['mob-bone-fish', 'bone-fish', 3],
    ['mob-rickel', 'rickel', 3],
    ['mob-berserkie', 'berserkie', 3],
    ['mob-dark-wyvern', 'dark-wyvern', 3],
  ] as const
).map(([key, file, scale]) => {
  const art = MONSTER_ART[key]();
  return slot({
    key,
    file: `monsters/${file}.png`,
    frames: art.frames.length,
    scale,
    grids: () => MONSTER_ART[key]().frames,
    palette: { ...BASE_PALETTE, ...art.palette },
    displayHeight: art.frameHeight * scale,
    group: 'monster',
  });
});

const TERRAIN_SLOTS: AssetSlot[] = [
  slot({
    key: 'tile-platform',
    file: 'terrain/platform.png',
    frames: 1,
    scale: 3,
    grids: () => [platformTile()],
    palette: TERRAIN_PALETTE,
    group: 'terrain',
  }),
  slot({
    key: 'tile-grass',
    file: 'terrain/grass.png',
    frames: 1,
    scale: 3,
    grids: () => [grassTile()],
    palette: TERRAIN_PALETTE,
    group: 'terrain',
  }),
  slot({
    key: 'tile-soil',
    file: 'terrain/soil.png',
    frames: 1,
    scale: 3,
    grids: () => [soilTile()],
    palette: TERRAIN_PALETTE,
    group: 'terrain',
  }),
  slot({
    key: 'climb-ladder',
    file: 'terrain/ladder.png',
    frames: 1,
    scale: 3,
    grids: () => [ladderTile()],
    palette: TERRAIN_PALETTE,
    group: 'terrain',
  }),
  slot({
    key: 'climb-rope',
    file: 'terrain/rope.png',
    frames: 1,
    scale: 3,
    grids: () => [ropeTile()],
    palette: TERRAIN_PALETTE,
    group: 'terrain',
  }),
  slot({
    key: 'portal',
    file: 'terrain/portal.png',
    frames: 4,
    scale: 3,
    grids: () => portalFrames(),
    palette: TERRAIN_PALETTE,
    group: 'terrain',
  }),
];

const DECOR_SLOTS: AssetSlot[] = [
  slot({
    key: 'decor-tree',
    file: 'decor/tree.png',
    frames: 1,
    scale: 3,
    grids: () => [treeArt()],
    palette: TERRAIN_PALETTE,
    group: 'decor',
  }),
  slot({
    key: 'decor-bush',
    file: 'decor/bush.png',
    frames: 1,
    scale: 3,
    grids: () => [bushArt()],
    palette: TERRAIN_PALETTE,
    group: 'decor',
  }),
  slot({
    key: 'decor-flower',
    file: 'decor/flower.png',
    frames: 1,
    scale: 3,
    grids: () => [flowerArt()],
    palette: TERRAIN_PALETTE,
    group: 'decor',
  }),
  slot({
    key: 'decor-signpost',
    file: 'decor/signpost.png',
    frames: 1,
    scale: 3,
    grids: () => [signpostArt()],
    palette: TERRAIN_PALETTE,
    group: 'decor',
  }),
  slot({
    key: 'decor-mushroom-house',
    file: 'decor/mushroom-house.png',
    frames: 1,
    scale: 3,
    grids: () => [mushroomHouseArt()],
    palette: TERRAIN_PALETTE,
    group: 'decor',
  }),
  slot({
    key: 'decor-big-mushroom',
    file: 'decor/big-mushroom.png',
    frames: 1,
    scale: 3,
    grids: () => [bigMushroomArt()],
    palette: TERRAIN_PALETTE,
    group: 'decor',
  }),
  slot({
    key: 'decor-cloud-a',
    file: 'decor/cloud-a.png',
    frames: 1,
    scale: 3,
    grids: () => [cloudArt(0)],
    palette: TERRAIN_PALETTE,
    group: 'decor',
  }),
  slot({
    key: 'decor-cloud-b',
    file: 'decor/cloud-b.png',
    frames: 1,
    scale: 3,
    grids: () => [cloudArt(1)],
    palette: TERRAIN_PALETTE,
    group: 'decor',
  }),
];

const ITEM_FILES: Record<string, string> = {
  'item-red-potion': 'items/red-potion.png',
  'item-orange-potion': 'items/orange-potion.png',
  'item-blue-potion': 'items/blue-potion.png',
  'item-white-potion': 'items/white-potion.png',
  'item-snail-shell': 'items/snail-shell.png',
  'item-blue-snail-shell': 'items/blue-snail-shell.png',
  'item-mushroom-cap': 'items/mushroom-cap.png',
  'item-horny-cap': 'items/horny-cap.png',
  'item-pig-ribbon': 'items/pig-ribbon.png',
  'item-slime-goo': 'items/slime-goo.png',
  'item-blue-slime-goo': 'items/blue-slime-goo.png',
  'item-tree-branch': 'items/tree-branch.png',
  'item-meso': 'items/meso.png',
  'item-sword-1h': 'items/sword-1h.png',
  'item-sword-2h': 'items/sword-2h.png',
  'item-dagger': 'items/dagger.png',
  'item-wand': 'items/wand.png',
  'item-bow': 'items/bow.png',
  'item-leather-cap': 'items/leather-cap.png',
  'item-orange-top': 'items/orange-top.png',
  'item-blue-bottom': 'items/blue-bottom.png',
  'item-brown-shoes': 'items/brown-shoes.png',
  'item-work-gloves': 'items/work-gloves.png',
  'item-white-cape': 'items/white-cape.png',
  'item-scroll-weapon': 'items/scroll-weapon.png',
  'item-scroll-armor': 'items/scroll-armor.png',
};

const ITEM_SLOTS: AssetSlot[] = Object.keys(ITEM_ART).map((key) =>
  slot({
    key,
    file: ITEM_FILES[key],
    frames: 1,
    scale: 2,
    grids: () => [itemGrid(ITEM_ART[key])],
    palette: ITEM_PALETTE,
    group: 'item',
  }),
);

const ICON_FILES: Record<string, string> = {
  'icon-attack': 'icons/attack.png',
  'icon-power-strike': 'icons/power-strike.png',
  'icon-slash-blast': 'icons/slash-blast.png',
  'icon-iron-body': 'icons/iron-body.png',
  'icon-energy-bolt': 'icons/energy-bolt.png',
  'icon-magic-claw': 'icons/magic-claw.png',
  'icon-bless': 'icons/bless.png',
  'icon-double-shot': 'icons/double-shot.png',
  'icon-arrow-blow': 'icons/arrow-blow.png',
  'icon-soul-arrow': 'icons/soul-arrow.png',
  'icon-lucky-seven': 'icons/lucky-seven.png',
  'icon-double-stab': 'icons/double-stab.png',
  'icon-haste': 'icons/haste.png',
  'icon-status-stun': 'icons/status-stun.png',
  'icon-status-slow': 'icons/status-slow.png',
  'icon-status-poison': 'icons/status-poison.png',
};

const ICON_SLOTS: AssetSlot[] = [...Object.keys(SKILL_ICON_ART), ...Object.keys(STATUS_ICON_ART)].map(
  (key) =>
    slot({
      key,
      file: ICON_FILES[key],
      frames: 1,
      scale: 2,
      grids: () => [(SKILL_ICON_ART[key] ?? STATUS_ICON_ART[key])()],
      palette: ITEM_PALETTE,
      group: 'icon',
    }),
);

const FX_SLOTS: AssetSlot[] = [
  slot({
    key: 'fx-hit',
    file: 'effects/hit.png',
    frames: 4,
    scale: 3,
    grids: () => hitFrames(),
    palette: FX_PALETTE,
    group: 'effect',
  }),
  slot({
    key: 'fx-slash',
    file: 'effects/slash.png',
    frames: 3,
    scale: 3,
    grids: () => slashFrames(false),
    palette: FX_PALETTE,
    group: 'effect',
  }),
  slot({
    key: 'fx-slash-strong',
    file: 'effects/slash-strong.png',
    frames: 3,
    scale: 3,
    grids: () => slashFrames(true),
    palette: FX_PALETTE,
    group: 'effect',
  }),
  slot({
    key: 'fx-blast',
    file: 'effects/blast.png',
    frames: 3,
    scale: 3,
    grids: () => blastFrames(),
    palette: FX_PALETTE,
    group: 'effect',
  }),
  slot({
    key: 'fx-level-up',
    file: 'effects/level-up.png',
    frames: 6,
    scale: 3,
    grids: () => levelUpFrames(),
    palette: FX_PALETTE,
    group: 'effect',
  }),
  slot({
    key: 'fx-spore',
    file: 'effects/spore.png',
    frames: 2,
    scale: 2,
    grids: () => sporeFrames(),
    palette: FX_PALETTE,
    group: 'effect',
  }),
  slot({
    key: 'fx-bolt',
    file: 'effects/bolt.png',
    frames: 2,
    scale: 2,
    grids: () => boltFrames(),
    palette: FX_PALETTE,
    group: 'effect',
  }),
  slot({
    key: 'fx-arrow',
    file: 'effects/arrow.png',
    frames: 2,
    scale: 2,
    grids: () => arrowFrames(),
    palette: FX_PALETTE,
    group: 'effect',
  }),
  slot({
    key: 'fx-star',
    file: 'effects/star.png',
    frames: 2,
    scale: 2,
    grids: () => starFrames(),
    palette: FX_PALETTE,
    group: 'effect',
  }),
];

const DAMAGE_FONT_PALETTE: Palette = {
  o: '#2b1f1a',
  A: '#fff6d8',
  a: '#f2c33c',
  B: '#ffd06a',
  b: '#ef5a2c',
  C: '#e3c4ff',
  c: '#8f4ad8',
};

const FONT_SLOTS: AssetSlot[] = [
  slot({
    key: 'font-damage',
    file: 'font/damage.png',
    frames: 10,
    scale: 2,
    grids: () => digitFont('A', 'a'),
    palette: DAMAGE_FONT_PALETTE,
    group: 'font',
  }),
  slot({
    key: 'font-damage-crit',
    file: 'font/damage-crit.png',
    frames: 10,
    scale: 2,
    grids: () => digitFont('B', 'b'),
    palette: DAMAGE_FONT_PALETTE,
    group: 'font',
  }),
  slot({
    key: 'font-damage-player',
    file: 'font/damage-player.png',
    frames: 10,
    scale: 2,
    grids: () => digitFont('C', 'c'),
    palette: DAMAGE_FONT_PALETTE,
    group: 'font',
  }),
  slot({
    key: 'word-miss',
    file: 'font/miss.png',
    frames: 1,
    scale: 2,
    grids: () => [missWord('A', 'a')],
    palette: DAMAGE_FONT_PALETTE,
    group: 'font',
  }),
];

const LOOK_SLOTS: AssetSlot[] = Object.keys(GEAR_LOOK_ART).map((key) =>
  slot({
    key,
    file: `look/${key.replace('look-', '')}.png`,
    frames: 1,
    scale: 3,
    grids: () => [GEAR_LOOK_ART[key]()],
    palette: BASE_PALETTE,
    displayHeight: 90,
    group: 'look',
  }),
);

export const ASSET_SLOTS: readonly AssetSlot[] = [
  ...PLAYER_SLOTS,
  ...MONSTER_SLOTS,
  ...TERRAIN_SLOTS,
  ...DECOR_SLOTS,
  ...ITEM_SLOTS,
  ...ICON_SLOTS,
  ...FX_SLOTS,
  ...FONT_SLOTS,
  ...LOOK_SLOTS,
];

/** 데미지 숫자 한 글자의 최종 픽셀 크기 (font 슬롯 scale 적용) */
export const DAMAGE_DIGIT_SIZE = {
  width: DIGIT_CELL.width * 2,
  height: DIGIT_CELL.height * 2,
};